/**
 * Enhanced Theme System with Multiple Theme Support
 * 
 * Features:
 * - Light mode (default)
 * - Dark Blue mode (navy/slate-based)
 * - Automatic time-based switching (optional)
 * - Manual theme cycling
 * - Persistent storage of preferences
 * - Visual indicators for current theme
 */

// Theme system state variables
let currentTheme = 'light'; // 'light', 'dark-blue'
let isAutoMode = true; // Default to automatic mode
let sunriseTime = null;
let sunsetTime = null;

// Theme configuration
const themes = {
  light: {
    name: 'Light',
    className: '',
    icon: 'fas fa-sun',
    description: 'Light theme'
  },
  'dark-blue': {
    name: 'Dark Blue',
    className: 'dark-blue',
    icon: 'fas fa-circle',
    description: 'Dark blue theme'
  }
};

/**
 * Cycle through available themes
 */
function cycleTheme() {
  const themeKeys = Object.keys(themes);
  const currentIndex = themeKeys.indexOf(currentTheme);
  const nextIndex = (currentIndex + 1) % themeKeys.length;
  
  // Switch to manual mode when user manually cycles themes
  isAutoMode = false;
  currentTheme = themeKeys[nextIndex];
  
  // Save preferences
  localStorage.setItem("themeAuto", "false");
  localStorage.setItem("currentTheme", currentTheme);
  
  applyTheme();
  updateThemeIcon();
}

/**
 * Set a specific theme
 * @param {string} themeName - Theme name ('light', 'dark-blue')
 */
function setTheme(themeName) {
  if (!themes[themeName]) {
    console.warn(`Theme "${themeName}" not found. Available themes:`, Object.keys(themes));
    return;
  }
  
  currentTheme = themeName;
  applyTheme();
  updateThemeIcon();
}

/**
 * Apply the current theme to the page
 */
function applyTheme() {
  const body = document.body;
  
  // Remove all theme classes
  Object.values(themes).forEach(theme => {
    if (theme.className) {
      body.classList.remove(theme.className);
    }
  });
  
  // Apply current theme class
  const themeConfig = themes[currentTheme];
  if (themeConfig.className) {
    body.classList.add(themeConfig.className);
  }
  
  console.log(`Applied theme: ${themeConfig.name}`);
}

/**
 * Update the theme toggle button icon and visual indicators
 */
function updateThemeIcon() {
  const themeIcon = document.getElementById("darkModeIcon"); // Reusing existing element
  const themeToggle = document.getElementById("darkModeToggle"); // Reusing existing element
  
  if (!themeIcon || !themeToggle) return;
  
  const themeConfig = themes[currentTheme];
  
  // Update icon
  themeIcon.innerHTML = `<i class="${themeConfig.icon}"></i>`;
  themeIcon.className = "text-xl theme-mode-icon";
  
  // Update accessibility labels and visual indicators
  if (isAutoMode) {
    themeToggle.title = `Auto ${themeConfig.name} Mode (based on time) - Click to cycle themes`;
    themeToggle.setAttribute("aria-label", `Currently in auto ${themeConfig.name.toLowerCase()} mode - Click to cycle themes`);
    themeToggle.style.boxShadow = "0 0 0 2px rgba(59, 130, 246, 0.5)"; // Blue glow for auto mode
  } else {
    const themeKeys = Object.keys(themes);
    const currentIndex = themeKeys.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    const nextTheme = themes[themeKeys[nextIndex]];
    
    themeToggle.title = `Switch to ${nextTheme.name} theme (currently ${themeConfig.name})`;
    themeToggle.setAttribute("aria-label", `Switch to ${nextTheme.name.toLowerCase()} theme`);
    themeToggle.style.boxShadow = "none";
  }
}

/**
 * Fetch sunrise and sunset times for Toronto from API
 */
async function fetchSunriseSunset() {
  try {
    const response = await fetch("https://api.sunrise-sunset.org/json?lat=43.7064&lng=-79.3986&formatted=0");
    const data = await response.json();
    
    if (data.status === "OK") {
      sunriseTime = new Date(data.results.sunrise);
      sunsetTime = new Date(data.results.sunset);
      
      console.log("Sunrise:", sunriseTime.toLocaleTimeString());
      console.log("Sunset:", sunsetTime.toLocaleTimeString());
      
      // Update theme based on current time if in auto mode
      if (isAutoMode) {
        updateThemeBasedOnTime();
      }
    }
  } catch (error) {
    console.error("Failed to fetch sunrise/sunset data:", error);
    // Fallback to saved preference if API fails
    initializeThemeFromStorage();
  }
}

/**
 * Update theme based on current time relative to sunrise/sunset
 * In auto mode: light during day, dark blue during night
 */
function updateThemeBasedOnTime() {
  if (!sunriseTime || !sunsetTime) return;
  
  const now = new Date();
  const thirtyMinutesMs = 30 * 60 * 1000; // 30 minutes in milliseconds
  
  // Calculate the times for theme switching
  const darkModeStartTime = new Date(sunsetTime.getTime() - thirtyMinutesMs); // 30 min before sunset
  const lightModeStartTime = new Date(sunriseTime.getTime() + thirtyMinutesMs); // 30 min after sunrise
  
  // Determine which theme should be active
  const shouldBeDark = now >= darkModeStartTime || now < lightModeStartTime;
  const newTheme = shouldBeDark ? 'dark-blue' : 'light'; // Use dark blue for nighttime auto mode
  
  if (newTheme !== currentTheme) {
    currentTheme = newTheme;
    applyTheme();
    updateThemeIcon();
    
    console.log(`Auto-switched to ${themes[currentTheme].name} theme at ${now.toLocaleTimeString()}`);
  }
}

/**
 * Initialize theme from localStorage or set up auto mode
 */
function initializeThemeFromStorage() {
  const savedAutoMode = localStorage.getItem("themeAuto");
  const savedTheme = localStorage.getItem("currentTheme");
  
  if (savedAutoMode === "false") {
    // Manual mode
    isAutoMode = false;
    currentTheme = savedTheme && themes[savedTheme] ? savedTheme : 'light';
  } else {
    // Auto mode (default)
    isAutoMode = true;
    // Will be set by updateThemeBasedOnTime() once sunrise/sunset data is loaded
    currentTheme = 'light'; // Default until time-based update
  }
  
  applyTheme();
  updateThemeIcon();
}

/**
 * Initialize theme system and fetch sunrise/sunset data
 */
async function initializeThemeSystem() {
  initializeThemeFromStorage();
  await fetchSunriseSunset();
  
  // Set up interval to check time every minute
  setInterval(() => {
    if (isAutoMode) {
      updateThemeBasedOnTime();
    }
  }, 60000); // Check every minute
  
  // Refresh sunrise/sunset data daily
  setInterval(fetchSunriseSunset, 24 * 60 * 60 * 1000); // Refresh every 24 hours
}

/**
 * Get current theme information
 * @returns {Object} Current theme configuration
 */
function getCurrentTheme() {
  return {
    name: currentTheme,
    config: themes[currentTheme],
    isAuto: isAutoMode
  };
}

/**
 * Enable auto mode (time-based theme switching)
 */
function enableAutoMode() {
  isAutoMode = true;
  localStorage.setItem("themeAuto", "true");
  updateThemeBasedOnTime();
  updateThemeIcon();
}

/**
 * Disable auto mode (manual theme selection)
 */
function disableAutoMode() {
  isAutoMode = false;
  localStorage.setItem("themeAuto", "false");
  localStorage.setItem("currentTheme", currentTheme);
  updateThemeIcon();
}

// Initialize theme system on page load
document.addEventListener("DOMContentLoaded", initializeThemeSystem);

// Make functions globally available for onclick handlers
window.toggleDarkMode = cycleTheme; // Reuse existing button handler
window.cycleTheme = cycleTheme;
window.setTheme = setTheme;
window.getCurrentTheme = getCurrentTheme;
window.enableAutoMode = enableAutoMode;
window.disableAutoMode = disableAutoMode;

// Export functions for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    cycleTheme,
    setTheme,
    getCurrentTheme,
    enableAutoMode,
    disableAutoMode,
    initializeThemeSystem,
    applyTheme,
    updateThemeIcon
  };
}